/**
 * Regras puras do Mapa — sem Google, sem rede, testáveis.
 */
import { type CalendarDate, compareCalendarDates } from '@nv/core';

/**
 * Depois disto a posição é "antiga": o app da pessoa não está mais aberto.
 * 15 min pelo refino visual final (PO, 24/09/2026; era 10).
 */
export const FRESCA_ATE_MS = 15 * 60_000;

export type Frescor = 'agora' | 'fresca' | 'antiga';

export function frescor(recordedAtIso: string, agora: number): Frescor {
  const idade = agora - Date.parse(recordedAtIso);
  if (idade < 60_000) return 'agora';
  if (idade < FRESCA_ATE_MS) return 'fresca';
  return 'antiga';
}

/** "agora" · "há 3 min" · "há 2 h" · "ontem" · "há 3 dias". */
export function haQuanto(recordedAtIso: string, agora: number): string {
  const idade = Math.max(0, agora - Date.parse(recordedAtIso));
  const min = Math.floor(idade / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'ontem' : `há ${d} dias`;
}

/**
 * Que dia o mapa mostra: hoje, se hoje está na viagem; antes dela, o primeiro
 * dia; depois, o último. Nunca um dia fora da viagem.
 */
export function diaDoMapa(
  dias: readonly { date: string }[],
  hoje: CalendarDate | null,
): string | null {
  if (dias.length === 0) return null;
  const ordenados = [...dias].sort((a, b) => a.date.localeCompare(b.date));
  const primeiro = ordenados[0]!.date;
  const ultimo = ordenados[ordenados.length - 1]!.date;
  if (!hoje) return primeiro;
  if (compareCalendarDates(hoje, primeiro as CalendarDate) < 0) return primeiro;
  if (compareCalendarDates(hoje, ultimo as CalendarDate) > 0) return ultimo;
  return ordenados.find((d) => d.date === hoje)?.date ?? primeiro;
}

export interface Ponto {
  latitude: number;
  longitude: number;
}

/** Caixa que contém todos os pontos, ou nula sem pontos. */
export function caixaDe(pontos: readonly Ponto[]): { norte: number; sul: number; leste: number; oeste: number } | null {
  if (pontos.length === 0) return null;
  let norte = -90;
  let sul = 90;
  let leste = -180;
  let oeste = 180;
  for (const p of pontos) {
    norte = Math.max(norte, p.latitude);
    sul = Math.min(sul, p.latitude);
    leste = Math.max(leste, p.longitude);
    oeste = Math.min(oeste, p.longitude);
  }
  return { norte, sul, leste, oeste };
}

/** Distância aproximada em metros (haversine). Serve para "andou o bastante para enviar". */
export function distanciaM(a: Ponto, b: Ponto): number {
  const R = 6_371_000;
  const rad = (g: number) => (g * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/** Enviar de novo só se passou tempo OU a pessoa andou: poupa bateria e rede. */
export const ENVIO_MINIMO_MS = 30_000;
export const DESLOCAMENTO_MINIMO_M = 25;

export function deveEnviar(
  ultimoEnvio: { at: number; ponto: Ponto } | null,
  leitura: { at: number; ponto: Ponto },
): boolean {
  if (!ultimoEnvio) return true;
  if (leitura.at - ultimoEnvio.at >= ENVIO_MINIMO_MS) return true;
  return distanciaM(ultimoEnvio.ponto, leitura.ponto) >= DESLOCAMENTO_MINIMO_M;
}

/** Iniciais para o pin: "Maria Silva" → "MS"; "Ana" → "A". */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]![0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}
